import { Modal } from '@components/core/modal.component';
import { Slider } from '@components/ui/slider';
import React, { ReactNode, useState } from 'react';
import { FormLabel } from './form-label.component';
import { ModalSidebar } from './modal-sidebar.component';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Props for the RadarModal component
 */
export type RadarModalProps = {
  children: (props: RadarModalRenderProps) => ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title: string;
};

/**
 * Render props function signature
 */
export type RadarModalRenderProps = {
  labelFontSize: number;
  size: number;
};

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_LABEL_FONT_SIZE = 20;
const DEFAULT_SIZE = 0.65;
const LABEL_FONT_SIZE_MAX = 48;
const LABEL_FONT_SIZE_MIN = 12;
const LABEL_FONT_SIZE_STEP = 2;
const MAX_SIZE = 1.5;
const MIN_SIZE = 0.3;
const SIZE_STEP = 0.05;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Fullscreen modal for Radar chart visualizations with size control
 */
export const RadarModal: React.FC<RadarModalProps> = (props) => {
  const { children, isOpen, onClose, title } = props;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [labelFontSize, setLabelFontSize] = useState<[number]>([DEFAULT_LABEL_FONT_SIZE]);
  const [size, setSize] = useState<[number]>([DEFAULT_SIZE]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      centered
      contentClassname="p-0 overflow-hidden"
      header={{
        title,
      }}
      isOpen={isOpen}
      onClose={onClose}
      width="95vw"
    >
      <div className="flex h-full w-full overflow-hidden">
        <ModalSidebar
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          sidebarContent={
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel label="Chart Size" />
                  <span className="text-sm text-gray-500">{Math.round(size[0] * 100)}%</span>
                </div>
                <Slider
                  className="w-full"
                  max={MAX_SIZE}
                  min={MIN_SIZE}
                  onValueChange={(value) => setSize([value[0]])}
                  step={SIZE_STEP}
                  value={size}
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <FormLabel label="Label Size" />
                  <span className="text-sm text-gray-500">{labelFontSize[0]}px</span>
                </div>
                <Slider
                  className="w-full"
                  max={LABEL_FONT_SIZE_MAX}
                  min={LABEL_FONT_SIZE_MIN}
                  onValueChange={(value) => setLabelFontSize([value[0]])}
                  step={LABEL_FONT_SIZE_STEP}
                  value={labelFontSize}
                />
              </div>
            </div>
          }
        >
          <div className="flex-1 flex items-center justify-center overflow- rounded-xl bg-gray-50 p-4">
            <div
              className="flex items-center justify-center"
              style={{ height: '70vh', maxHeight: '70vh', maxWidth: '70vh', width: '70vh' }}
            >
              {children({ labelFontSize: labelFontSize[0], size: size[0] })}
            </div>
          </div>
        </ModalSidebar>
      </div>
    </Modal>
  );
};
